import { useContext } from "react";
import { Link } from "react-router-dom";
import "../styles/Sidebar.css";

import MenuToggle from "./MenuToggle";
import MenuItem from "./MenuItem";
import MenuHead from "./MenuHead";
import { AuthContext } from "../context/AuthContext";
import { PERMISSIONS } from "../config/permissions";
import { hasAnyPermission, hasPermission } from "../utils/permissions";

const Sidebar = ({ mobileOpen = false, onClose }) => {
    const { user } = useContext(AuthContext);
    const can = (permission) => hasPermission(user, permission);
    const canAny = (permissions) => hasAnyPermission(user, permissions);
    const canFinanceCash = can(PERMISSIONS.FINANCE_CAISSE);
    const canFinancePayroll = can(PERMISSIONS.FINANCE_PAIE);
    const canOpenPayrollMenu = canFinancePayroll || canFinanceCash;

    const financePermissions = [
        PERMISSIONS.FINANCE_MANAGER,
        PERMISSIONS.FINANCE_FACTURATION,
        PERMISSIONS.FINANCE_CAISSE,
        PERMISSIONS.FINANCE_COMPTABILITE,
        PERMISSIONS.FINANCE_FINANCE,
        PERMISSIONS.ERP_BUDGETS,
        PERMISSIONS.FINANCE_PAIE,
    ];
    const approPermissions = [PERMISSIONS.APPRO_MANAGER, PERMISSIONS.APPRO_REQUISITIONS, PERMISSIONS.APPRO_FACTURATION, PERMISSIONS.APPRO_MAGASIN, PERMISSIONS.APPRO_CARBURANT];
    const rhPermissions = [
        PERMISSIONS.RH_MANAGER,
        PERMISSIONS.RH_SERVICES_GENERAUX,
        PERMISSIONS.RH_BATIMENT,
        PERMISSIONS.RH_PERSONNEL,
        PERMISSIONS.RH_BIENS,
        PERMISSIONS.RH_RECEPTION,
    ];
    const operationsPermissions = [PERMISSIONS.OPERATIONS_MANAGER, PERMISSIONS.OPERATIONS_TRANSPORT, PERMISSIONS.OPERATIONS_MAINTENANCE, PERMISSIONS.OPERATIONS_SPS, PERMISSIONS.OPERATIONS_DEMANDES];
    const itPermissions = [
        PERMISSIONS.TEAM_IT_MANAGER,
        PERMISSIONS.TEAM_IT_PROGRAMMATION,
        PERMISSIONS.TEAM_IT_RESEAU,
        PERMISSIONS.TEAM_IT_PROJET,
        PERMISSIONS.TEAM_IT_DESIGN,
    ];
    const hssePermissions = [PERMISSIONS.HSSE_MANAGER, PERMISSIONS.HSSE_GARDES, PERMISSIONS.HSSE_ESCORTE];
    const managerPermissions = [
        PERMISSIONS.FINANCE_MANAGER,
        PERMISSIONS.APPRO_MANAGER,
        PERMISSIONS.RH_MANAGER,
        PERMISSIONS.OPERATIONS_MANAGER,
        PERMISSIONS.TEAM_IT_MANAGER,
        PERMISSIONS.HSSE_MANAGER,
        PERMISSIONS.DG_MANAGER,
    ];
    const profileLabel = can(PERMISSIONS.ADMIN) ? "Admin" : canAny(managerPermissions) ? "Manager" : "User";

    const handleSidebarClick = (event) => {
        if (event.target.closest("a.menu-link")) {
            onClose?.();
        }
    };

    return (
        <aside
            id="layout-menu"
            className={`layout-menu menu-vertical menu bg-menu-theme ${mobileOpen ? "show" : ""}`}
            onClick={handleSidebarClick}
        >
            <div className="app-brand demo">
                <Link to="/dashboard" className="app-brand-link">
                    <span className="brand-logo-frame">
                        <img src="/assets/img/brand/soma-logo-red-blue.png" alt="Logo SOMA Sarl" className="logo logo-light" />
                        <img src="/assets/img/brand/soma-logo-white-red.png" alt="Logo SOMA Sarl" className="logo logo-dark" />
                    </span>
                    <span className="brand-copy">
                        <strong>SOMA MASTER</strong>
                        <span>Operations hub</span>
                    </span>
                </Link>
                <button className="sidebar-mobile-close" type="button" aria-label="Fermer le menu" onClick={onClose}>
                    <i className="bx bx-x"></i>
                </button>
            </div>

            <div className="sidebar-mini-stats">
                <div>
                    <span>Profil</span>
                    <strong>{profileLabel}</strong>
                </div>
                <div>
                    <span>Controle</span>
                    <strong>OK</strong>
                </div>
            </div>

            <div className="menu-inner-shadow" />

            <div className="sidebar-scroll">
                <ul className="menu-inner py-1">
                    <MenuHead label="Accueil" />
                    <MenuToggle label="Pilotage" icon="bx-grid-alt">
                        <MenuItem to="/dashboard" label="Tableau de bord" />
                        {can(PERMISSIONS.ERP_COCKPIT) && <MenuItem to="/erp_cockpit" label="Cockpit ERP" />}
                        {can(PERMISSIONS.ERP_VALIDATIONS) && <MenuItem to="/erp_validations" label="Centre de validation" />}
                        {can(PERMISSIONS.ERP_NOTIFICATIONS) && <MenuItem to="/erp_notifications" label="Notifications" />}
                        {can(PERMISSIONS.ERP_DOCUMENTS) && <MenuItem to="/erp_documents" label="Documents et archives" />}
                        {can(PERMISSIONS.ERP_AUDIT) && <MenuItem to="/erp_audit" label="Audit et tracabilite" />}
                        {can(PERMISSIONS.ERP_SECURITY) && <MenuItem to="/erp_security" label="Actions sensibles" />}
                        {can(PERMISSIONS.ERP_BACKUPS) && <MenuItem to="/erp_backups" label="Sauvegardes" />}
                        {can(PERMISSIONS.ERP_RISKS) && <MenuItem to="/erp_risks" label="Risques" />}
                        {can(PERMISSIONS.ERP_REPORTS) && <MenuItem to="/erp_reports" label="Rapports programmes" />}
                        {can(PERMISSIONS.ERP_ASSISTANT) && <MenuItem to="/erp_assistant" label="Assistant interne" />}
                    </MenuToggle>
                    <MenuToggle label="Communication" icon="bx-envelope">
                        <MenuItem to="/mail" label="Messagerie" />
                    </MenuToggle>

                    {can(PERMISSIONS.ADMIN) && (
                        <>
                            <MenuHead label="Admin" />
                            <MenuToggle label="Administration" icon="bx-shield-quarter">
                                <MenuItem to="/admin_users" label="Utilisateurs" />
                                <MenuItem to="/admin_roles" label="Roles" />
                            </MenuToggle>
                        </>
                    )}

                    {canAny(financePermissions) && (
                        <>
                            <MenuHead label="Direction Finances" />
                            {can(PERMISSIONS.FINANCE_FACTURATION) && (
                                <MenuToggle label="Facturation" icon="bx-receipt">
                                    <MenuItem to="/fin_fact_dashboard" label="Tableau de bord" />
                                    <MenuItem to="/fin_fact_entreprise" label="Entreprises" />
                                    <MenuItem to="/fin_fact_consulation" label="Archives factures" />
                                    <MenuItem to="/fin_fact_printing" label="Situation des factures" />
                                </MenuToggle>
                            )}
                            {canFinanceCash && (
                                <MenuToggle label="Caisse" icon="bx-wallet">
                                    <MenuItem to="/fin_cash_dashboard" label="Tableau de bord" />
                                    <MenuItem to="/fin_cash_exchange_rate" label="Taux d'echange" />
                                    <MenuItem to="/fin_cash_movement" label="Mouvements caisse" />
                                    <MenuItem to="/fin_cash_payment_register" label="caisse paiement" />
                                    <MenuItem to="/fin_cash_prints" label="Impressions" />
                                    <MenuItem to="/fin_cash_payments" label="Paiement caisse" />
                                    <MenuItem to="/fin_cash_execution_plans" label="Plans a executer" />
                                    <MenuItem to="/fin_cash_requisitions" label="Requisitions" />
                                </MenuToggle>
                            )}
                            {can(PERMISSIONS.FINANCE_COMPTABILITE) && (
                                <MenuToggle label="Comptabilite" icon="bx-calculator">
                                    <MenuItem to="/fin_comptabilite_dashboard" label="Tableau de bord" />
                                    <MenuItem to="/fin_comptabilite" label="Ecritures comptables" />
                                </MenuToggle>
                            )}
                            {(can(PERMISSIONS.FINANCE_FINANCE) || can(PERMISSIONS.FINANCE_MANAGER) || can(PERMISSIONS.ERP_BUDGETS)) && (
                                <MenuToggle label="Finance" icon="bx-line-chart">
                                    {(can(PERMISSIONS.FINANCE_FINANCE) || can(PERMISSIONS.FINANCE_MANAGER)) && <MenuItem to="/fin_finance_dashboard" label="Tableau de bord" />}
                                    {(can(PERMISSIONS.FINANCE_FINANCE) || can(PERMISSIONS.FINANCE_MANAGER)) && <MenuItem to="/fin_requisitions" label="Nouvelle requisition" />}
                                    {(can(PERMISSIONS.FINANCE_FINANCE) || can(PERMISSIONS.FINANCE_MANAGER)) && <MenuItem to="/fin_requisitions_list" label="Liste requisitions" />}
                                    {(can(PERMISSIONS.FINANCE_FINANCE) || can(PERMISSIONS.FINANCE_MANAGER)) && <MenuItem to="/fin_procurement_requests" label="Requisitions departements" />}
                                    {(can(PERMISSIONS.FINANCE_FINANCE) || can(PERMISSIONS.FINANCE_MANAGER)) && <MenuItem to="/fin_execution_plans" label="Plan d'execution" />}
                                    {(can(PERMISSIONS.FINANCE_FINANCE) || can(PERMISSIONS.ERP_BUDGETS)) && <MenuItem to="/fin_budgets" label="Budgets" />}
                                </MenuToggle>
                            )}
                            {canOpenPayrollMenu && (
                                <MenuToggle label="Paie" icon="bx-credit-card-front">
                                    {canFinancePayroll && <MenuItem to="/fin_payroll_dashboard" label="Tableau de bord" />}
                                    {canFinancePayroll && <MenuItem to="/fin_payroll_config" label="Configuration" />}
                                    <MenuItem to="/fin_payroll_employee" label="Employe" />
                                    <MenuItem to="/fin_payroll_employees" label="Liste des employes" />
                                    {canFinancePayroll && <MenuItem to="/fin_payroll_deductions" label="Retenues" />}
                                    {canFinancePayroll && <MenuItem to="/fin_payroll_prepare" label="Preparation paie" />}
                                    {canFinancePayroll && <MenuItem to="/fin_payroll_salary_mass" label="Masse salariale" />}
                                    <MenuItem to="/fin_payroll_cash_prepare" label="Liste paiement caisse" />
                                </MenuToggle>
                            )}
                        </>
                    )}

                    {canAny(approPermissions) && (
                        <>
                            <MenuHead label="Direction Approvisionnements" />
                            {can(PERMISSIONS.APPRO_FACTURATION) && (
                                <MenuToggle label="Facturation" icon="bx-package">
                                    <MenuItem to="/app_fact_entreprise" label="Entreprises" />
                                    <MenuItem to="/app_fact_consulation" label="Archives factures" />
                                    <MenuItem to="/app_fact_printing" label="Situation des factures" />
                                </MenuToggle>
                            )}
                            {can(PERMISSIONS.APPRO_MAGASIN) && (
                                <MenuToggle label="Magasin" icon="bx-store">
                                    <MenuItem to="/app_magasin" label="Magasin" />
                                </MenuToggle>
                            )}
                            {(can(PERMISSIONS.APPRO_MAGASIN) || can(PERMISSIONS.APPRO_MANAGER) || can(PERMISSIONS.APPRO_REQUISITIONS)) && (
                                <MenuToggle label="Requisitions" icon="bx-git-pull-request">
                                    <MenuItem to="/app_requisitions" label="Nouvelle requisition" />
                                    <MenuItem to="/app_requisitions_list" label="Liste requisitions" />
                                    <MenuItem to="/app_requisition_transfers" label="Demandes recues" />
                                </MenuToggle>
                            )}
                            {can(PERMISSIONS.APPRO_CARBURANT) && (
                                <MenuToggle label="Carburant" icon="bx-gas-pump">
                                    <MenuItem to="/app_carburant" label="Nouveau bon" />
                                    <MenuItem to="/app_carburant_journal" label="Journal carburant" />
                                </MenuToggle>
                            )}
                        </>
                    )}

                    {canAny(rhPermissions) && (
                        <>
                            <MenuHead label="RH" />
                            <MenuToggle label="Ressources Humaines" icon="bx-group">
                                <MenuItem to="/rh_requisitions" label="Nouvelle requisition" />
                                <MenuItem to="/rh_requisitions_list" label="Liste requisitions" />
                                <MenuItem to="/rh_fuel_request" label="Demande carburant" />
                                <MenuItem to="/rh_fuel_list" label="Liste carburant" />
                                {can(PERMISSIONS.RH_SERVICES_GENERAUX) && <MenuItem to="/rh_services_generaux" label="Services Generaux" />}
                                {can(PERMISSIONS.RH_BATIMENT) && <MenuItem to="/rh_batiment" label="Gestion du Batiment" />}
                                {can(PERMISSIONS.RH_BIENS) && <MenuItem to="/rh_biens" label="Gestion des biens" />}
                                {can(PERMISSIONS.RH_RECEPTION) && <MenuItem to="/rh_reception" label="Reception" />}
                            </MenuToggle>
                            {can(PERMISSIONS.RH_PERSONNEL) && (
                                <MenuToggle label="Gestion du personnel" icon="bx-id-card">
                                    <MenuItem to="/rh_personnel_dashboard" label="Tableau de bord" />
                                    <MenuItem to="/rh_personnel_employees" label="Employes" />
                                    <MenuItem to="/rh_personnel_assignments" label="Affectations" />
                                    <MenuItem to="/rh_personnel_contracts" label="Contrats" />
                                    <MenuItem to="/rh_personnel_attendance" label="Presences" />
                                    <MenuItem to="/rh_personnel_leaves" label="Conges" />
                                    <MenuItem to="/rh_personnel_documents" label="Documents" />
                                    <MenuItem to="/rh_personnel_sanctions" label="Sanctions et avertissements" />
                                    <MenuItem to="/rh_personnel_evaluations" label="Evaluations" />
                                    <MenuItem to="/rh_personnel_trainings" label="Formations" />
                                    <MenuItem to="/rh_personnel_career" label="Carriere" />
                                    <MenuItem to="/rh_personnel_exits" label="Sortie du personnel" />
                                    <MenuItem to="/rh_personnel_alerts" label="Alertes automatiques" />
                                    <MenuItem to="/rh_personnel_reports" label="Rapports" />
                                </MenuToggle>
                            )}
                        </>
                    )}

                    {canAny(operationsPermissions) && (
                        <>
                            <MenuHead label="Direction Operations" />
                            <MenuToggle label="Operations" icon="bx-trip">
                                {can(PERMISSIONS.OPERATIONS_TRANSPORT) && <MenuItem to="/ops_transport" label="Service Transport" />}
                                {can(PERMISSIONS.OPERATIONS_MAINTENANCE) && <MenuItem to="/ops_maintenance" label="Pannes" />}
                                {can(PERMISSIONS.OPERATIONS_SPS) && <MenuItem to="/ops_sps" label="SPS" />}
                                {can(PERMISSIONS.OPERATIONS_SPS) && <MenuItem to="/ops_sps_list" label="Liste SPS" />}
                                {can(PERMISSIONS.OPERATIONS_SPS) && <MenuItem to="/ops_chomage" label="Chomage" />}
                                {can(PERMISSIONS.OPERATIONS_SPS) && <MenuItem to="/ops_chomage_list" label="Liste chomage" />}
                                {can(PERMISSIONS.OPERATIONS_TRANSPORT) && <MenuItem to="/ops_trucksolid" label="Trucksolid Pro" />}
                            </MenuToggle>
                            {canAny(operationsPermissions) && (
                                <MenuToggle label="Demande" icon="bx-file">
                                    {(can(PERMISSIONS.OPERATIONS_DEMANDES) || can(PERMISSIONS.OPERATIONS_MANAGER)) && <MenuItem to="/ops_requests_new" label="Nouvelle demande" />}
                                    {(can(PERMISSIONS.OPERATIONS_DEMANDES) || can(PERMISSIONS.OPERATIONS_MANAGER)) && <MenuItem to="/ops_requests_list" label="Liste des courses" />}
                                    <MenuItem to="/ops_requisitions" label="Nouvelle requisition" />
                                    <MenuItem to="/ops_requisitions_list" label="Liste requisitions" />
                                </MenuToggle>
                            )}
                        </>
                    )}

                    {canAny(itPermissions) && (
                        <>
                            <MenuHead label="Team IT" />
                            <MenuToggle label="Team IT" icon="bx-code-alt">
                                <MenuItem to="/it_requisitions" label="Nouvelle requisition" />
                                <MenuItem to="/it_requisitions_list" label="Liste requisitions" />
                                {can(PERMISSIONS.TEAM_IT_PROGRAMMATION) && <MenuItem to="/it_programmation" label="Programmation" />}
                                {can(PERMISSIONS.TEAM_IT_RESEAU) && <MenuItem to="/it_reseau" label="Reseau" />}
                                {can(PERMISSIONS.TEAM_IT_PROJET) && <MenuItem to="/it_projet" label="Projet" />}
                                {can(PERMISSIONS.TEAM_IT_DESIGN) && <MenuItem to="/it_design" label="Design" />}
                                {can(PERMISSIONS.TEAM_IT_RESEAU) && <MenuItem to="/it_trucksolid" label="Trucksolid Pro" />}
                                {(can(PERMISSIONS.TEAM_IT_MANAGER) || can(PERMISSIONS.TEAM_IT_PROGRAMMATION)) && <MenuItem to="/it_systeme" label="Controle systeme" />}
                                {(can(PERMISSIONS.TEAM_IT_MANAGER) || can(PERMISSIONS.TEAM_IT_PROGRAMMATION)) && <MenuItem to="/it_audit" label="Journal des activites" />}
                            </MenuToggle>
                        </>
                    )}

                    {canAny(hssePermissions) && (
                        <>
                            <MenuHead label="Direction HSSE" />
                            <MenuToggle label="HSSE" icon="bx-shield">
                                <MenuItem to="/hsse_requisitions" label="Nouvelle requisition" />
                                <MenuItem to="/hsse_requisitions_list" label="Liste requisitions" />
                                {can(PERMISSIONS.HSSE_GARDES) && <MenuItem to="/hsse_gardes" label="Gardes" />}
                                {can(PERMISSIONS.HSSE_ESCORTE) && <MenuItem to="/hsse_escorte" label="Escorte" />}
                            </MenuToggle>
                        </>
                    )}

                    {(can(PERMISSIONS.DG_SECRETARIAT) || can(PERMISSIONS.DG_MANAGER)) && (
                        <>
                            <MenuHead label="Direction Generale" />
                            <MenuToggle label="Direction Generale" icon="bx-building-house">
                                {can(PERMISSIONS.DG_SECRETARIAT) && <MenuItem to="/dg_secretariat" label="Secretariat" />}
                                <MenuItem to="/dg_requisitions" label="Nouvelle requisition" />
                                <MenuItem to="/dg_requisitions_list" label="Liste requisitions" />
                            </MenuToggle>
                        </>
                    )}
                </ul>
            </div>

            <div className="sidebar-status-card">
                <span className="sidebar-status-icon">
                    <i className="bx bx-shield-quarter"></i>
                </span>
                <div>
                    <strong>Systeme actif</strong>
                    <span>Acces interne securise</span>
                </div>
            </div>
        </aside>
    );
};

export default Sidebar;
